// CORE
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { timeout, switchMap, map, catchError } from "rxjs/operators";

// SERVICE
import { LocalStorageService } from "./../local-storage/local-storage.service";
import { UrlService } from "./../url.service";
import { CacheService } from "./../cache/cache.service";

// Models
import { Country } from "./../../../entities/shared/Country";
import { environment } from "src/environments/environment";
import { IAbmBuscador } from "./../../../interfaces/pages/abm/abm-buscador.interface";
import { objBodyConsumer, objBodyCommercial } from "./../../../entities/shared/BodysKl";
import { SearchCountry } from './../../../components/shared/home-searcher/data-table/SearchCountry';

@Injectable({
  providedIn: "root",
})
export class BuscadorService {

  get countrySelected(): Country {
    return this.localStorageService.getRegionSeleccionada;
  }

  get buscadorSelect(): IAbmBuscador {
    return this.localStorageService.getBuscadorSelect;
  }

  // TTL de 5 minutos para busquedas - datos que pueden cambiar con ediciones ABM
  private readonly BUSCADOR_CACHE_TTL = 5 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private urlService: UrlService,
    private cacheService: CacheService,
  ) { }

  // Servicio para llamar desde los componentes, determina el criterio de búsqueda
  searchData(inputSearch: number | string): Observable<any> {
    const { isHash, domain, subDomain, profile } = this.buscadorSelect;
    const serviceCountry: string = this.countrySelected.getService().toLowerCase();
    const findProfile: SearchCountry = this.countrySelected.getUrlFindFull().find((profile) => (profile.subDomain === subDomain && profile.domain === domain));
    console.log("this.countrySelected.getUrlFindFull() ------>", this.countrySelected.getUrlFindFull());
    this.localStorageService.documentList = "";
    /**
     * @constant validKl valida:
     * El servicio de la region en el archivo COUNTRIES.json sea GKL
     * Si existe un profile asociado al subDominion en el archivo COUNTRIES.json
     * Que en la configuracion de ABM no sea hash
     * Con esto el if determina si usar endpoint klService o lastMk
    */
    const typeProfile = (profile !== null && profile !== "null")
    const validKl: boolean = serviceCountry === 'gkl' && findProfile && typeProfile && !isHash;
    if (environment.IUP_DEL_CONSUMO_GET_PERSON_BY_ID.toString().toLowerCase() === "true") {
      if (validKl) {
        return this.getKlService(inputSearch);
      } else {
        return this.getStandarSearch(inputSearch);
      }
    } else {
      return this.getMockDataService();
    }
  }

  getStandarSearch(inputSearch: number | string, findProfile?): Observable<any> {
    const { isHash, subDomain } = findProfile ? findProfile : this.buscadorSelect;
    if (isHash) {
      // LLama servicio de encoding y con la respuesta llama a lastMk
      return this.getEncodeMkService(inputSearch).pipe(
        switchMap((encodeValue: { hash: string }) => {
          console.log("Hash inputValue: ", encodeValue.hash);
          this.localStorageService.setPersonaSelectedDniHash = encodeValue.hash;
          return this.getLastMkService(encodeValue.hash, findProfile).pipe(
            map((response: any) => {
              if (response) {
                let keyFuenteXX = Object.keys(response?.payload)[0];
                const validaKeyName = keyFuenteXX === subDomain;
                const persons: any[] = validaKeyName ? response.payload[keyFuenteXX] : response.payload;
                if (persons?.length > 0) {
                  for (const persona of persons) {
                    persona['hash'] = encodeValue.hash;
                  }
                }
              }
              return response;
            })
          );
        })
      );
    } else {
      // Llama servicio lastMk
      return this.getLastMkService(inputSearch, findProfile);
    }
  }

  getLastMkService(mkValue: string | number, findProfile?) {
    const { domain, subDomain } = findProfile ? findProfile : this.buscadorSelect;
    const country = this.countrySelected.getCode().toUpperCase();

    /** Variables URL*/
    const rutaMicroservicio = (String(environment.IS_LOCAL.toLowerCase()) === 'true')
      ? "/journaling-service"
      : (environment.MICROSERVICIO.toString().toLowerCase() === "true")
      ? "/" + (this.localStorageService.getIsVip
        ? 'journaling-service'
        : this.localStorageService.configInit["F_URL_MS_IM_JOURNALING"])
      : "";
    const urlApi = `/v3/sources/lastmk/${country}/${domain}/${subDomain}`;
    const urlService = this.urlService.getURL(rutaMicroservicio) + urlApi;
    const mkValueService = new HttpParams().set('matchkey', mkValue + '.');

    // Llamada al servicio
    return this.http.get<any>(urlService, { params: mkValueService })
      .pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  searchDocumentList(mkValue: string | number, options?: { silent?: boolean }) {
    const { domain, subDomain } = this.buscadorSelect;
    const country = this.countrySelected.getCode().toUpperCase();

    /** Variables URL*/
    const rutaMicroservicio = (String(environment.IS_LOCAL.toLowerCase()) === 'true')
      ? "/journaling-service"
      : (environment.MICROSERVICIO.toString().toLowerCase() === "true")
      ? "/" + (this.localStorageService.getIsVip
        ? 'journaling-service'
        : this.localStorageService.configInit["F_URL_MS_IM_JOURNALING"])
      : "";
    const urlApi = `/v1/sources/documents/${country}/${domain}/${subDomain}`;
    const urlService = this.urlService.getURL(rutaMicroservicio) + urlApi;
    const mkValueService = new HttpParams().set('matchkey', String(mkValue));

    const requestOptions: { params: HttpParams; headers?: HttpHeaders } = {
      params: mkValueService
    };

    if (options?.silent) {
      requestOptions.headers = new HttpHeaders({
        'x-skip-spinner': 'true'
      });
    }

    // Llamada al servicio
    return this.http.get<any>(urlService, requestOptions)
      .pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  getKlService(inputSearch: number | string) {
    const { domain, subDomain, identifyType, profileBusqueda, typeNumber, profile } = this.buscadorSelect;
    const country = this.countrySelected.getCode().toUpperCase();

    /** Variables URL*/
    const rutaMicroservicio = environment.MICROSERVICIO.toString().toLowerCase() === "true"
      ? "/" + (this.localStorageService.getIsVip
        ? 'kl-service'
        : this.localStorageService.configInit["F_URL_MS_IM_KL"])
      : "";
    const urlApi = this.localStorageService.getIsVip ?
      '/v1/KL/getEntityById' :
      `/v2/KL/getEntityById/${country}/${domain}/${subDomain}`;
    const urlService = this.urlService.getURL(rutaMicroservicio) + urlApi;
    const bodyKl = this.getBodyKl(inputSearch, identifyType, undefined, profileBusqueda, typeNumber, profile);

    // Llamada al servicio
    return this.http.post<any>(urlService, bodyKl)
      .pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  getKlPurposingData(inputSearch: number | string, purposingConfig: IAbmBuscador): Observable<any> {
    const country = this.countrySelected.getCode().toUpperCase();
    const rutaMicroservicio = environment.MICROSERVICIO.toString().toLowerCase() === "true"
      ? "/" + (this.localStorageService.getIsVip
        ? 'kl-service'
        : this.localStorageService.configInit["F_URL_MS_IM_KL"])
      : "";
    const profile = (purposingConfig?.profile === 'COMMERCIAL' || purposingConfig?.profile === 'CONSUMER')
      ? purposingConfig.profile
      : 'CONSUMER';
    const domain = purposingConfig?.domain || '';
    const subDomain = purposingConfig?.purposingProfile || purposingConfig?.subDomain || '';
    const urlApi = `/v3/KL/getEntityById/${country}/${domain}/${subDomain}`;
    const urlService = this.urlService.getURL(rutaMicroservicio) + urlApi;

    const bodyKl = this.getBodyKl(
      inputSearch,
      purposingConfig?.identifyType || '',
      undefined,
      purposingConfig?.profileBusqueda || '',
      purposingConfig?.typeNumber || '',
      profile
    );

    if (!bodyKl) {
      return of('');
    }

    const responseValues = this.getPurposingResponseValues(purposingConfig);
    const bodyWithPurp = {
      ...bodyKl,
      ...(Object.keys(responseValues).length > 0 ? { responseValues } : {}),
      purp: true
    };

    return this.http.post<any>(urlService, bodyWithPurp).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      catchError(() => of(''))
    );
  }

  private getPurposingResponseValues(purposingConfig: IAbmBuscador): Record<string, any> {
    const filterPayload = purposingConfig?.purposingFilterPayload;
    if (!filterPayload) {
      return {};
    }

    try {
      const parsedPayload = typeof filterPayload === 'string'
        ? JSON.parse(filterPayload)
        : filterPayload;

      return parsedPayload && typeof parsedPayload === 'object' && !Array.isArray(parsedPayload)
        ? parsedPayload
        : {};
    } catch {
      return {};
    }
  }

  private extractEntityKeyFromKlResponse(response: any): string {
    const payload = response?.payload ?? response;

    const fromEntityKeys = payload?.entityKeys?.[0]?.keyId;
    if (fromEntityKeys) {
      return String(fromEntityKeys).trim();
    }

    const directKeys = [
      payload?.keyId,
      payload?.entityKey,
      response?.keyId,
      response?.entityKey,
    ];
    const directValue = directKeys.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
    if (directValue) {
      return String(directValue).trim();
    }

    const nestedValue = this.findFirstStringValue(payload, ['keyId', 'entityKey']);
    return nestedValue || '';
  }

  private findFirstStringValue(source: any, keys: string[]): string {
    if (source === null || source === undefined) {
      return '';
    }

    if (Array.isArray(source)) {
      for (const item of source) {
        const value = this.findFirstStringValue(item, keys);
        if (value) {
          return value;
        }
      }
      return '';
    }

    if (typeof source !== 'object') {
      return '';
    }

    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }

    for (const objectKey of Object.keys(source)) {
      const value = this.findFirstStringValue(source[objectKey], keys);
      if (value) {
        return value;
      }
    }

    return '';
  }

  getEncodeMkService(inputVaue: number | string): Observable<{ hash: string }> {
    // Validate input not empty
    if (!inputVaue || inputVaue.toString().trim() === '') {
      return throwError(() => new Error('El valor para hash no puede ser vacio'));
    }

    // Variables URL
    const rutaMicroservicio = environment.MICROSERVICIO.toString().toLowerCase() === "true"
      ? "/" + (this.localStorageService.getIsVip
        ? 'gcp-service'
        : this.localStorageService.configInit["F_URL_MS_IM_GCP"])
      : "";
    // comment const urlService = `${this.urlService.getURL("/datamaintenance-service")}/v1/hashing/${inputVaue.toString()}/${this.countrySelected.getCode().toUpperCase()}`;
    const urlService = `${this.urlService.getURL(rutaMicroservicio)}/v1/hash/${inputVaue?.toString()}/${this.countrySelected.getCode().toUpperCase()}`;
    // Llamada al servicio
    return this.http.get<{ hash: string }>(urlService)
      .pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  getMockDataService() {
    const { subDomain } = this.buscadorSelect;
    const url = `assets/mock/${this.countrySelected.getCode().toLowerCase()}/preview/DATA_${subDomain}.json`;
    // Llamada al servicio
    return this.http.get<any>(url);
  }

  // Profile para llamadas GKL
  getBodyKl(documento: number | string, identifierType: string, type?, profileBusqueda?: string, typeNumber?: string, profile?: string): {} {
    const countryUpper = this.countrySelected.getCode().toLocaleUpperCase();
    profileBusqueda = (profileBusqueda == null) ? "" : profileBusqueda;
    if (profile === "COMMERCIAL") {
      return objBodyCommercial(documento, identifierType, profileBusqueda, countryUpper, typeNumber);
    } else if (profile === "CONSUMER") {
      return objBodyConsumer(documento, identifierType, profileBusqueda, countryUpper, typeNumber);
    }
  }

  searchDataAll(inputSearch: number | string, typeSearch): Observable<any> {
    const { isHash, domain, subDomain } = typeSearch;


    const serviceCountry: string = this.countrySelected.getService().toLowerCase();

    const findProfile: SearchCountry = this.countrySelected.getUrlFindFull().find((profile) => (profile.subDomain === subDomain && profile.domain === domain));
    console.log("searchDataAll findProfile ---->", findProfile);
    /**
     * @constant validKl valida:
     * El servicio de la region en el archivo COUNTRIES.json sea GKL
     * Si existe un profile asociado al subDominion en el archivo COUNTRIES.json
     * Que en la configuracion de ABM no sea hash
     * Con esto el if determina si usar endpoint klService o lastMk
    */

    const validKl: boolean = serviceCountry === 'gkl' && findProfile && !isHash;

    if (environment.IUP_DEL_CONSUMO_GET_PERSON_BY_ID.toString().toLowerCase() === "true") {
      if (findProfile !== undefined) {
        if (validKl) {
          return this.getKlServiceAll(inputSearch, typeSearch, findProfile);
        } else {
          return this.getStandarSearch(inputSearch, typeSearch);
        }
      } else {
        return of(
          {
            "message": null,
            "errors": null,
            "payload": {
              "key": []
            }
          }
        )
      }
    } else {
      return this.getMockDataService();
    }
  }

  getKlServiceAll(inputSearch: number | string, typeSearch: any, findProfile: any) {
    const { domain, subDomain, identifyType, profileBusqueda, typeNumber, profile } = typeSearch;
    const country = this.countrySelected.getCode().toUpperCase();
    const { type } = findProfile;
    /** Variables URL*/
    const rutaMicroservicio = environment.MICROSERVICIO.toString().toLowerCase() === "true"
      ? "/" + (this.localStorageService.getIsVip
        ? 'kl-service'
        : this.localStorageService.configInit["F_URL_MS_IM_KL"])
      : "";
    const urlApi = this.localStorageService.getIsVip
      ? '/v1/KL/getEntityById'
      : `/v2/KL/getEntityById/${country}/${domain}/${subDomain}`;
    const urlService = this.urlService.getURL(rutaMicroservicio) + urlApi;
    const bodyKl = this.getBodyKl(inputSearch, identifyType, type, profileBusqueda, typeNumber, profile);

    // Llamada al servicio
    return this.http.post<any>(urlService, bodyKl)
      .pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)), catchError(err => of({ error: true, err, payload: { error: [] } })));
  }


  getKlServiceUYName(value: any, profile: string, domain?: string, subDomain?: string, profileId?: string): Observable<any> {

    const country = this.countrySelected.getCode().toUpperCase();

    console.log("getKlServiceUYName profile --->", profile);

    console.log("getKlServiceUYName profileId --->", profileId);


    const rutaMicroservicio = environment.MICROSERVICIO.toString().toLowerCase() === "true"
      ? "/" + (this.localStorageService.getIsVip
        ? 'kl-service'
        : this.localStorageService.configInit["F_URL_MS_IM_KL"])
      : "";
    const urlApi = this.localStorageService.getIsVip ?
      '/v1/KL/getEntityById' :
      `/v2/KL/getEntityById/${country}/${domain}/${subDomain}`;
    const urlService = environment.IS_LOCAL === "true" ?
      `assets/mock/${country.toLocaleLowerCase()}/preview/DATA_NAME_${subDomain}.json`
      : this.urlService.getURL(rutaMicroservicio) + urlApi;


    const key = profile.toLocaleLowerCase();

    let data = {};
    if (profile === 'CONSUMER') {
      data = {
        "NAME_FIRST_NAME": String(value["name"]).toUpperCase() || '',// 'MARIA',
        "NAME_MIDDLE_NAME": String(value["secondName"]).toUpperCase() || '',//"TERESA",
        "NAME_LAST_NAME": String(value["surname"]).toUpperCase() || '', // "MARTINEZ",
        "NAME_SECOND_LAST_NAME": String(value["secondSurname"]).toUpperCase() || '' // "GIMENEZ"
      }
    } else {
      data = {
        "NAME_LEGAL_BUSINESS_NAME": String(value["companyName"]).toUpperCase() || '',
        "NAME_BUSINESS_NAME": ""
      };
    }

    const bodyKl = {
      profileId,
      [key]: {
        "names": [
          {
            data
          }],
        "identifiers": {
          "IDENTIFIER_ORIGIN_COUNTRY_CODE": country.toLocaleUpperCase()
        }
      }
    }

    console.log("getKlServiceUYName bodyKl --->", bodyKl);

    return environment.IS_LOCAL === "true" ? this.http.get(urlService) : this.http.post<any>(urlService, bodyKl)
      .pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  // buscador.service.ts

getKlAdvance(
  namesData: any,
  identifiersData: any, // Nuevo parámetro
  profile: string,
  domain?: string,
  subDomain?: string,
  profileId?: string
): Observable<any> {

  const country = this.countrySelected.getCode().toUpperCase();
  const rutaMicroservicio = environment.MICROSERVICIO.toString().toLowerCase() === "true"
    ? "/" + (this.localStorageService.getIsVip ? 'kl-service' : this.localStorageService.configInit["F_URL_MS_IM_KL"])
    : "";

  const urlApi = this.localStorageService.getIsVip ?
    '/v1/KL/getEntityById' :
    `/v2/KL/getEntityById/${country}/${domain}/${subDomain}`;

  const urlService = environment.IS_LOCAL === "true" ?
    `assets/mock/${country.toLocaleLowerCase()}/preview/DATA_NAME_${subDomain}.json`
    : this.urlService.getURL(rutaMicroservicio) + urlApi;


  const entityKey = profile.toLocaleLowerCase();


  const bodyKl = {
    profileId: profileId || "",
    [entityKey]: {}
  };


  if (namesData && Object.keys(namesData).length > 0) {
    bodyKl[entityKey]["names"] = [
      {
        "data": namesData
      }
    ];
  }


  if (identifiersData && Object.keys(identifiersData).length > 0) {
    bodyKl[entityKey]["identifiers"] = identifiersData;
  }

  console.log("getKlAdvance Body Dinámico Generado --->", JSON.stringify(bodyKl, null, 2));

  return environment.IS_LOCAL === "true"
    ? this.http.get(urlService)
    : this.http.post<any>(urlService, bodyKl).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
}

  get country(): string {
    return this.localStorageService.getRegionSeleccionada?.getCode()?.toUpperCase();
  }

  /** Invalida todo el cache de buscador */
  invalidateBuscadorCache(): void {
    this.cacheService.invalidateByPrefix('buscador|');
  }

}
